/**
 * The page layout every PDF in the system shares - claims, monthly reports and
 * verification certificates alike.
 *
 * KEEP THIS FILE IDENTICAL in the backend (src/utils/pdfLayout.mjs) and the
 * frontend (src/utils/pdfLayout.mjs). The server-signed report, the browser's
 * fallback report and the certificate a reviewer downloads are meant to be the
 * same document; one layout in two places is how they stay that way. Nothing
 * here touches the filesystem, the network or the DOM - the logo bytes and the
 * WinAnsi sanitiser are passed in, because each side gets them differently.
 *
 * What a page looks like:
 *   page one      the Stanford Seed logo (the one in the navbar), the title,
 *                 an optional subtitle and a cardinal rule
 *   later pages   a cardinal band naming the document
 *   every page    a footer: generated time, reference, "Page X of Y"
 *
 * Building blocks: info() details panel, heading() section title, table() a
 * ruled table whose header row repeats on every page, callout() a coloured box,
 * note() a wrapped paragraph, signatureBlocks() signature stamps. Call finish()
 * last - the footers need the final page count.
 */
import { StandardFonts, rgb } from 'pdf-lib';

export const PDF_TONES = {
  CARDINAL: rgb(0.549, 0.082, 0.082),
  INK: rgb(0.12, 0.12, 0.12),
  MUTED: rgb(0.42, 0.42, 0.42),
  RULE: rgb(0.84, 0.84, 0.84),
  ZEBRA: rgb(0.965, 0.965, 0.965),
  PANEL: rgb(0.955, 0.955, 0.955),
  TINT: rgb(0.985, 0.93, 0.93),
  WHITE: rgb(1, 1, 1),
  GREEN: rgb(0.1, 0.45, 0.2),
  GREEN_BG: rgb(0.92, 0.97, 0.93),
  RED: rgb(0.7, 0.1, 0.1),
  RED_BG: rgb(0.99, 0.92, 0.92),
};

/** A4 landscape, in points. */
export const A4_LANDSCAPE = [841.89, 595.28];
/** US Letter landscape, in points. */
export const LETTER_LANDSCAPE = [792, 612];

/** PNG or JPEG, from the file's own first bytes. */
const imageKind = (bytes) => {
  if (!bytes || bytes.length < 4) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'jpg';
  return null;
};

/**
 * @param {PDFDocument} pdf
 * @param {object}  o
 * @param {string}  o.title        shown under the logo and in the page band
 * @param {string} [o.subtitle]    a line under the title
 * @param {string} [o.bandRef]     right-hand text of the continuation band
 * @param {string} [o.footerRef]   middle of the footer, e.g. "Claim ID ..."
 * @param {string} [o.footerNote]
 * @param {Uint8Array} [o.logo]    the navbar logo's bytes (PNG or JPEG)
 * @param {Function} [o.sanitize]  string -> WinAnsi-safe string
 * @param {number[]} [o.pageSize]
 * @param {number}  [o.margin]
 * @param {Date}    [o.generatedAt]
 */
export async function createPdfLayout(pdf, {
  title,
  subtitle = '',
  bandRef = '',
  footerRef = '',
  footerNote = 'Stanford Seed Expense Reimbursement System',
  logo = null,
  sanitize = (v) => String(v ?? ''),
  pageSize = A4_LANDSCAPE,
  margin = 36,
  generatedAt = new Date(),
}) {
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const mono = await pdf.embedFont(StandardFonts.Courier);
  const C = PDF_TONES;

  const [W, H] = pageSize;
  const M = margin;
  const usable = W - M * 2;
  const FOOT = 30;   // kept clear at the foot of every page for the footer

  // The standard fonts only encode WinAnsi; one stray character fails the file.
  const clean = (v) => sanitize(v === null || v === undefined ? '' : String(v));

  let image = null;
  const kind = imageKind(logo);
  if (kind) {
    try {
      image = kind === 'png' ? await pdf.embedPng(logo) : await pdf.embedJpg(logo);
    } catch {
      image = null;
    }
  }

  // ---------------------------------------------------------------- text
  const widthOf = (str, f, size) => f.widthOfTextAtSize(str, size);

  const fitLine = (str, f, size, maxW) => {
    let out = str;
    if (widthOf(out, f, size) <= maxW) return out;
    while (out.length > 1 && widthOf(`${out}...`, f, size) > maxW) out = out.slice(0, -1);
    return `${out}...`;
  };

  /**
   * Word-wrapped into at most `maxLines`. An explicit newline starts a new
   * line; a word too wide for the cell (a PTAE code, a hash) breaks after a
   * dot, dash or slash first and by character only if it must.
   */
  const wrap = (value, f, size, maxW, maxLines = 1) => {
    const raw = value === null || value === undefined ? '' : String(value);
    const lines = [];
    for (const paragraph of raw.split('\n')) {
      const words = clean(paragraph).split(/\s+/).filter(Boolean);
      let cur = '';
      for (const word of words) {
        const candidate = cur ? `${cur} ${word}` : word;
        if (widthOf(candidate, f, size) <= maxW) { cur = candidate; continue; }
        if (cur) lines.push(cur);
        if (widthOf(word, f, size) <= maxW) { cur = word; continue; }
        cur = '';
        for (const piece of word.split(/(?<=[.\-/])/)) {
          if (widthOf(cur + piece, f, size) <= maxW) { cur += piece; continue; }
          if (cur) lines.push(cur);
          cur = '';
          for (const ch of piece) {
            if (cur && widthOf(cur + ch, f, size) > maxW) { lines.push(cur); cur = ch; } else cur += ch;
          }
        }
      }
      if (cur) lines.push(cur);
    }
    if (lines.length === 0) return [''];
    if (lines.length > maxLines) {
      const kept = lines.slice(0, maxLines);
      kept[maxLines - 1] = fitLine(`${kept[maxLines - 1]} ${lines[maxLines]}`, f, size, maxW);
      return kept;
    }
    return lines;
  };

  const draw = (pg, str, x, yy, { f = font, size = 9, colour = C.INK } = {}) =>
    pg.drawText(str, { x, y: yy, size, font: f, color: colour });

  // ---------------------------------------------------------------- pages
  let page;
  let y;

  const band = () => {
    page.drawRectangle({ x: 0, y: H - 24, width: W, height: 24, color: C.CARDINAL });
    draw(page, fitLine(clean(`Stanford Seed  -  ${title}`), bold, 8.5, usable * 0.7), M, H - 15.5,
      { f: bold, size: 8.5, colour: C.WHITE });
    if (bandRef) {
      const right = fitLine(clean(bandRef), font, 8, usable * 0.28);
      draw(page, right, W - M - widthOf(right, font, 8), H - 15.5, { size: 8, colour: C.WHITE });
    }
  };

  const newPage = () => {
    page = pdf.addPage([W, H]);
    band();
    y = H - 24 - 18;
  };

  const room = (h) => y - h >= M + FOOT;

  // Page one: the navbar logo, the title, the rule.
  page = pdf.addPage([W, H]);
  y = H - M + 6;
  if (image) {
    const lh = 50;
    const lw = (image.width / image.height) * lh;
    page.drawImage(image, { x: (W - lw) / 2, y: y - lh, width: lw, height: lh });
    y -= lh + 12;
  } else {
    const t = 'Stanford Seed';
    draw(page, t, (W - widthOf(t, bold, 16)) / 2, y - 16, { f: bold, size: 16, colour: C.CARDINAL });
    y -= 30;
  }
  const t1 = fitLine(clean(title), bold, 14, usable);
  draw(page, t1, (W - widthOf(t1, bold, 14)) / 2, y - 12, { f: bold, size: 14, colour: C.CARDINAL });
  y -= 20;
  if (subtitle) {
    const t2 = fitLine(clean(subtitle), font, 10.5, usable);
    draw(page, t2, (W - widthOf(t2, font, 10.5)) / 2, y - 9, { size: 10.5, colour: C.MUTED });
    y -= 16;
  }
  y -= 2;
  page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 1.2, color: C.CARDINAL });
  y -= 16;

  // ---------------------------------------------------------------- blocks
  /**
   * Two columns of label / value. An entry whose third element is
   * `{ wide: true }` (a business purpose) gets a full-width wrapped row below.
   */
  const info = (entries = []) => {
    if (!entries.length) return;
    const grid = entries.filter(entry => !entry[2]?.wide);
    const wide = entries.filter(entry => entry[2]?.wide);
    const perCol = Math.ceil(grid.length / 2);
    const rowH = 15;
    const colW = usable / 2;
    const labelW = 108;
    const wideLines = wide.map(([, value]) => wrap(value ?? '-', font, 9, usable - labelW - 30, 3));
    const wideH = wideLines.reduce((t, ls) => t + Math.max(1, ls.length) * 12 + 4, 0);
    const boxH = perCol * rowH + 12 + wideH;
    if (!room(boxH + 10)) newPage();
    page.drawRectangle({
      x: M, y: y - boxH, width: usable, height: boxH,
      color: C.PANEL, borderColor: C.RULE, borderWidth: 0.75,
    });
    grid.forEach(([label, value], i) => {
      const col = i < perCol ? 0 : 1;
      const r = col === 0 ? i : i - perCol;
      const x = M + 10 + col * colW;
      const ty = y - 16 - r * rowH;
      draw(page, fitLine(clean(label), bold, 8.5, labelW - 6), x, ty, { f: bold, size: 8.5, colour: C.MUTED });
      const v = wrap(value ?? '-', font, 9, colW - labelW - 20, 1)[0] || '-';
      draw(page, v, x + labelW, ty, { size: 9 });
    });
    let wy = y - 16 - perCol * rowH;
    wide.forEach(([label], i) => {
      draw(page, fitLine(clean(label), bold, 8.5, labelW - 6), M + 10, wy, { f: bold, size: 8.5, colour: C.MUTED });
      wideLines[i].forEach((ln, li) => draw(page, ln, M + 10 + labelW, wy - li * 12, { size: 9 }));
      wy -= Math.max(1, wideLines[i].length) * 12 + 4;
    });
    y -= boxH + 18;
  };

  /** A section title, moved to a new page rather than stranded at the foot. */
  const heading = (label, needBelow = 40) => {
    if (!room(22 + needBelow)) newPage();
    draw(page, clean(label), M, y - 9, { f: bold, size: 9.5, colour: C.CARDINAL });
    y -= 14;
    page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 0.75, color: C.CARDINAL });
    y -= 6;
  };

  /**
   * A ruled table. Columns: { label, w (percent of width), align: 'right',
   * wrap: max lines, mono: fixed-width }. A cell is a value, or
   * { text, colour, bold } to colour one cell (a VERIFIED / FAILED status).
   * The header row repeats on every new page, rows grow to their tallest
   * wrapped cell, alternate rows are shaded, and `footer: [label, value]`
   * adds a highlighted total row.
   */
  const table = (cols, body, { width = usable, size = 8, footer = null, empty = 'None recorded.' } = {}) => {
    const widths = cols.map(c => (c.w / 100) * width);
    const PAD = 5;
    const norm = (cell) => (cell && typeof cell === 'object' && !Array.isArray(cell) ? cell : { text: cell });
    const fontFor = (c, cell) => (cell.bold ? bold : (c.mono ? mono : font));
    // Fixed-width text runs wide: a 64-character hash fits two lines at this size.
    const sizeFor = (c) => (c.mono ? size - 1.5 : size);

    const head = () => {
      const hh = 18;
      if (!room(hh + size + 12)) newPage();
      page.drawRectangle({ x: M, y: y - hh, width, height: hh, color: C.CARDINAL });
      let x = M;
      cols.forEach((c, i) => {
        const label = fitLine(clean(c.label), bold, 7.5, widths[i] - PAD * 2);
        const tx = c.align === 'right' ? x + widths[i] - PAD - widthOf(label, bold, 7.5) : x + PAD;
        draw(page, label, tx, y - 12, { f: bold, size: 7.5, colour: C.WHITE });
        x += widths[i];
      });
      y -= hh;
    };

    head();
    if (body.length === 0) {
      draw(page, clean(empty), M + PAD, y - 12, { size: 8.5, colour: C.MUTED });
      y -= 18;
    }
    body.forEach((cells, ri) => {
      const prepared = cols.map((c, i) => {
        const cell = norm(cells[i]);
        const f = fontFor(c, cell);
        const s = sizeFor(c);
        return { cell, f, s, lines: wrap(cell.text, f, s, widths[i] - PAD * 2, c.wrap || 1) };
      });
      const rh = Math.max(...prepared.map(p => p.lines.length * (p.s + 2.5))) + 6;
      if (!room(rh)) { newPage(); head(); }
      if (ri % 2 === 1) page.drawRectangle({ x: M, y: y - rh, width, height: rh, color: C.ZEBRA });
      let x = M;
      prepared.forEach((p, i) => {
        p.lines.forEach((ln, li) => {
          const tx = cols[i].align === 'right' ? x + widths[i] - PAD - widthOf(ln, p.f, p.s) : x + PAD;
          draw(page, ln, tx, y - 4 - p.s - li * (p.s + 2.5), { f: p.f, size: p.s, colour: p.cell.colour || C.INK });
        });
        x += widths[i];
      });
      page.drawLine({ start: { x: M, y: y - rh }, end: { x: M + width, y: y - rh }, thickness: 0.5, color: C.RULE });
      y -= rh;
    });

    if (footer) {
      const fh = 20;
      if (!room(fh)) newPage();
      page.drawRectangle({ x: M, y: y - fh, width, height: fh, color: C.TINT });
      page.drawLine({ start: { x: M, y }, end: { x: M + width, y }, thickness: 1, color: C.CARDINAL });
      const [label, value] = footer;
      draw(page, clean(label), M + PAD, y - 13.5, { f: bold, size: 9.5, colour: C.CARDINAL });
      const v = clean(value);
      draw(page, v, M + width - PAD - widthOf(v, bold, 10), y - 13.5, { f: bold, size: 10, colour: C.CARDINAL });
      y -= fh;
    }
    y -= 16;
  };

  /**
   * A coloured box - the integrity verdict. `lines` are strings or
   * { text, mono, muted }.
   */
  const callout = ({ tone = 'neutral', title: boxTitle, lines = [] }) => {
    const fg = tone === 'success' ? C.GREEN : tone === 'danger' ? C.RED : C.INK;
    const bg = tone === 'success' ? C.GREEN_BG : tone === 'danger' ? C.RED_BG : C.PANEL;
    const inner = usable - 28;
    const prepared = lines.map(l => (typeof l === 'string' ? { text: l } : l)).map(l => {
      const f = l.mono ? mono : font;
      const s = l.mono ? 7.5 : 9;
      return { ...l, f, s, wrapped: wrap(l.text, f, s, inner, 3) };
    });
    const bodyH = prepared.reduce((t, l) => t + l.wrapped.length * (l.s + 3) + 2, 0);
    const boxH = 16 + 18 + bodyH + 8;
    if (!room(boxH + 8)) newPage();
    page.drawRectangle({
      x: M, y: y - boxH, width: usable, height: boxH,
      color: bg, borderColor: fg, borderWidth: 1.5,
    });
    let ty = y - 22;
    draw(page, clean(boxTitle), M + 14, ty, { f: bold, size: 12, colour: fg });
    ty -= 18;
    for (const l of prepared) {
      for (const ln of l.wrapped) {
        draw(page, ln, M + 14, ty, { f: l.f, size: l.s, colour: l.muted ? C.MUTED : fg });
        ty -= l.s + 3;
      }
      ty -= 2;
    }
    y -= boxH + 16;
  };

  /**
   * Signature stamps, two to a row, in the shape Adobe Sign prints them: the
   * signer's mark on the left, who / why / when beside it, the stage hash
   * along the foot. A block is { label, image, fallback, alert, lines, hash }:
   *   image     an embedded PDFImage (embedPng is async, so the caller embeds
   *             it first), or null
   *   fallback  text in place of a missing image; alert:true draws it in red
   *   lines     strings or { text, bold, muted, alert, wrap }
   */
  const signatureBlocks = (blocks = []) => {
    const GAP = 12;
    const PAD = 8;
    const LINE = 10.5;
    const cardW = (usable - GAP) / 2;
    const imgW = 150;
    const imgH = 56;
    const textX = PAD + imgW + 12;
    const textW = cardW - textX - PAD;

    const prepared = blocks.map(b => ({
      ...b,
      textLines: (b.lines || [])
        .map(l => (typeof l === 'string' ? { text: l } : l))
        .filter(l => l && l.text)
        .flatMap(l => {
          const f = l.bold || l.alert ? bold : font;
          return wrap(l.text, f, 8, textW, l.wrap || 1)
            .map(text => ({ text, f, colour: l.alert ? C.RED : (l.muted ? C.MUTED : C.INK) }));
        }),
    }));

    // One card, its top edge at `top`.
    const stamp = (b, x, top, cardH) => {
      const bodyTop = top - 18;
      page.drawRectangle({
        x, y: top - cardH, width: cardW, height: cardH,
        color: C.WHITE, borderColor: C.RULE, borderWidth: 0.75,
      });
      draw(page, fitLine(clean(b.label), bold, 8, cardW - PAD * 2), x + PAD, top - 13,
        { f: bold, size: 8, colour: C.CARDINAL });

      const ix = x + PAD;
      if (b.image) {
        const scale = Math.min(imgW / b.image.width, imgH / b.image.height);
        const w = b.image.width * scale;
        const h = b.image.height * scale;
        page.drawImage(b.image, { x: ix + (imgW - w) / 2, y: bodyTop - imgH + (imgH - h) / 2, width: w, height: h });
      } else if (b.fallback) {
        const f = b.alert ? bold : font;
        wrap(b.fallback, f, 8, imgW, 3).forEach((ln, li) =>
          draw(page, ln, ix, bodyTop - 12 - li * LINE, { f, size: 8, colour: b.alert ? C.RED : C.MUTED }));
      }
      page.drawLine({
        start: { x: ix, y: bodyTop - imgH - 2 }, end: { x: ix + imgW, y: bodyTop - imgH - 2 },
        thickness: 0.5, color: C.RULE,
      });

      b.textLines.forEach((l, li) =>
        draw(page, l.text, x + textX, bodyTop - 9 - li * LINE, { f: l.f, size: 8, colour: l.colour }));

      if (b.hash) {
        draw(page, fitLine(clean(b.hash), mono, 6.5, cardW - PAD * 2), x + PAD, top - cardH + 7,
          { f: mono, size: 6.5, colour: C.MUTED });
      }
    };

    for (let i = 0; i < prepared.length; i += 2) {
      const row = prepared.slice(i, i + 2);
      const bodyH = Math.max(imgH, ...row.map(b => b.textLines.length * LINE));
      const cardH = 18 + bodyH + 8 + (row.some(b => b.hash) ? 12 : 0);
      if (!room(cardH + 10)) newPage();
      for (let j = 0; j < row.length; j += 1) stamp(row[j], M + j * (cardW + GAP), y, cardH);
      y -= cardH + 10;
    }
  };

  /** A wrapped paragraph across the page. */
  const note = (text, { muted = true, size = 8.5 } = {}) => {
    const lines = wrap(text, font, size, usable, 12);
    const h = lines.length * (size + 3);
    if (!room(h + 6)) newPage();
    lines.forEach((ln, i) => draw(page, ln, M, y - size - i * (size + 3), { size, colour: muted ? C.MUTED : C.INK }));
    y -= h + 10;
  };

  /** Footers on every page, now that the page count is known. */
  const finish = () => {
    const pages = pdf.getPages();
    const stamp = `${generatedAt.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
    pages.forEach((pg, i) => {
      pg.drawLine({ start: { x: M, y: 24 }, end: { x: W - M, y: 24 }, thickness: 0.5, color: C.RULE });
      const left = clean([`Generated ${stamp}`, footerRef, footerNote].filter(Boolean).join('  |  '));
      draw(pg, fitLine(left, font, 7, usable - 80), M, 13, { size: 7, colour: C.MUTED });
      const right = `Page ${i + 1} of ${pages.length}`;
      draw(pg, right, W - M - widthOf(right, font, 7), 13, { size: 7, colour: C.MUTED });
    });
  };

  return {
    info, heading, table, callout, note, signatureBlocks, newPage, finish,
    usableWidth: usable,
    spacer: (n) => { y -= n; },
  };
}

const pdfLayout = { createPdfLayout, PDF_TONES, A4_LANDSCAPE, LETTER_LANDSCAPE };
export default pdfLayout;
